import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { Bindable } from "bindable-data";
import { NotifyProperty } from "bindable-data";
import { nameof } from "bindable-data";
import { IPlayerModel } from "./IPlayerModel";
import { PlayerDto } from "../../../networking/data/responses/user/PlayerDto";
import { BuildingModel } from "./Building/BuildingModel";
import { INetworkClient } from "../../../networking/INetworkClient";
import { IBuildingModel } from "./Building/IBuildingModel";
import { AuthorizedViaDto } from "by/gamefactory/cityversetycoon/networking/data/responses/user/AuthorizedViaDto";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { BuildingRightsType } from "../../../networking/data/responses/user/BuildingRightsType";
import Big from "big.js";
import { IConnectionModel } from "../../../networking/IConnectionModel";

@injectable()
export class PlayerModel extends Bindable implements IPlayerModel {
    private static readonly BATCH_SIZE = 10;
    public readonly coins: NotifyProperty<number>;
    public readonly tokens: NotifyProperty<string>;
    public readonly rolls: NotifyProperty<number>;
    public readonly shields: NotifyProperty<number>;
    public readonly buildings: NotifyProperty<Array<IBuildingModel>>;
    public readonly hasBuildingFixes: NotifyProperty<boolean>;

    public readonly level: NotifyProperty<number>;
    public readonly nextLevel: NotifyProperty<number>;
    public readonly authorizedVia: NotifyProperty<AuthorizedViaDto>;

    public get isAuthorized(): boolean {
        const authorizedViaValue = this.authorizedVia.value;
        return authorizedViaValue?.email
            || authorizedViaValue?.facebook
            || authorizedViaValue?.apple
            || authorizedViaValue?.web3;
    }

    public get openedPiggyBank(): boolean {
        return this.buildings.value?.some((building) => building?.userLevelUpModel.value?.openedPiggyBank) ?? false;
    }

    public readonly upgradePointsBalance: NotifyProperty<number>;
    public readonly requiredPointsBalance: NotifyProperty<number>;
    public readonly buildingRights: NotifyProperty<BuildingRightsType>;
    public readonly lastLeaderboardPlace: NotifyProperty<number>;

    private readonly networkClient: INetworkClient;
    private readonly connectionModel: IConnectionModel;
    private readonly logger: ILogger;

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.networkClient = networkClient;
        this.connectionModel = connectionModel;
        this.logger = logger;
        this.coins = new NotifyProperty<number>(this, nameof(this, "coins"), 0);
        this.tokens = new NotifyProperty<string>(this, nameof(this, "tokens"), undefined);
        this.rolls = new NotifyProperty<number>(this, nameof(this, "rolls"), 1);
        this.shields = new NotifyProperty<number>(this, nameof(this, "shields"), 0);
        this.buildings = new NotifyProperty<Array<IBuildingModel>>(this, nameof(this, "buildings"), []);
        this.hasBuildingFixes = new NotifyProperty<boolean>(this, nameof(this, "hasBuildingFixes"), false);
        this.level = new NotifyProperty<number>(this, nameof(this, "level"), 0);
        this.nextLevel = new NotifyProperty<number>(this, nameof(this, "nextLevel"), 0);
        this.authorizedVia = new NotifyProperty<AuthorizedViaDto>(this, nameof(this, "authorizedVia"), undefined);
        this.upgradePointsBalance = new NotifyProperty<number>(this, nameof(this, "upgradePointsBalance"), 0);
        this.requiredPointsBalance = new NotifyProperty<number>(this, nameof(this, "requiredPointsBalance"), 0);
        this.buildingRights = new NotifyProperty<BuildingRightsType>(this, nameof(this, "buildingRights"), BuildingRightsType.RENTER);
        this.lastLeaderboardPlace = new NotifyProperty<number>(this, nameof(this, "lastLeaderboardPlace"), 0);
    }

    @initializer()
    public async initialization(): Promise<void> {
        try {
            await this.loadPlayerData();
        } catch (error: unknown) {
            this.logger.error(error);
        }
    }

    public async loadBuildings(withUpgrades: boolean = true): Promise<void> {
        try {
            if (!this.connectionModel.connected.value) {
                return;
            }
            this.logger.debug(`[BUILDINGS WORKFLOW] Load buildings (upgrades - ${withUpgrades})`);
            const guestBuildingsDto = await this.networkClient.getGuestBuildings();
            if (guestBuildingsDto.items == undefined) {
                throw new Error("Failed to load guest buildings.");
            }
            this.logger.debug(`[BUILDINGS WORKFLOW] Create building models for ${JSON.stringify(guestBuildingsDto.items)}`);
            const newBuildings = await Promise.all(guestBuildingsDto.items.map((buildingDto) => BuildingModel.create(this.networkClient, buildingDto, this.logger, withUpgrades)));
            this.buildings.setValue(newBuildings);
    
            this.checkAndUpdateBuildingHasFixes();
        }
        catch(e) {
            this.logger.error(`[BUILDINGS WORKFLOW] Exception on PlayerModel.loadBuildings: `, e);
        }
    }

    public async loadPlayerData(): Promise<void> {
        if (!this.connectionModel.connected.value) {
            return;
        }
        const playerDto = await this.networkClient.getPlayerData();
        if (playerDto == undefined) {
            throw new Error("Failed to load guest playerDto.");
        }
        this.coins.setValue(playerDto.coinsBalance);
        this.tokens.setValue(playerDto.tokensBalance);
        this.rolls.setValue(playerDto.diceRollsBalance);
        this.shields.setValue(playerDto.shieldsBalance);
        this.level.setValue(playerDto.level);
        this.nextLevel.setValue(playerDto.nextLevel);
        this.authorizedVia.setValue(playerDto.authorizedVia);
        this.requiredPointsBalance.setValue(playerDto.requiredPointsBalance);
        this.upgradePointsBalance.setValue(playerDto.upgradePointsBalance);
        this.buildingRights.setValue(playerDto.buildingRights);
        this.lastLeaderboardPlace.setValue(playerDto.lastLeaderboardPlace);
    }

    public addCoins(coinsValue: number): void {
        this.coins.setValue(this.coins.value + coinsValue);
    }

    public addTokens(tokensValue: string): void {
        this.tokens.setValue(new Big(this.tokens.value ?? 0).plus(tokensValue).toString());
    }

    public addRolls(rollsValue: number): void {
        this.rolls.setValue(this.rolls.value + rollsValue);
    }

    public addShields(shieldsValue: number): void {
        this.shields.setValue(this.shields.value + shieldsValue);
    }

    public subtractRolls(): void {
        if (this.rolls.value === 0) {
            //throw new Error("Rolls value was 0.");
            return;
        }
        this.rolls.setValue(this.rolls.value - 1);
    }

    public subtractCoins(coinsValue: number): void {
        if (this.coins.value === 0) {
            throw new Error("Coins value was 0.")
        }
        this.coins.setValue(this.coins.value - coinsValue);
    }

    public subtractTokens(tokensValue: string): void {
        if (this.tokens.value === "" || this.tokens.value === "0.0" || this.tokens.value === "0") {
            throw new Error("Tokens value was 0.")
        }
        this.tokens.setValue(new Big(this.tokens.value).minus(tokensValue).toString());
    }

    public subtractShields(value: number): void {
        if (this.shields.value === 0) {
            //throw new Error("Shields value was 0.");
            return;
        }
        this.shields.setValue(this.shields.value - value);
    }

    public updateValuesFromUpgrade(dto: PlayerDto): void {
        if (this.level.value != dto.level) {
            this.level.setValue(dto.level);
        }
        if (this.nextLevel.value != dto.nextLevel) {
            this.nextLevel.setValue(dto.nextLevel);
        }
        if (this.upgradePointsBalance.value != dto.upgradePointsBalance) {
            this.upgradePointsBalance.setValue(dto.upgradePointsBalance);
        }
        if (this.requiredPointsBalance.value != dto.requiredPointsBalance) {
            this.requiredPointsBalance.setValue(dto.requiredPointsBalance);
        }
        if (this.buildingRights.value != dto.buildingRights) {
            this.buildingRights.setValue(dto.buildingRights);
        }
    }

    public checkAndUpdateBuildingHasFixes(): void {
        if (this.buildings.value.length > 0) {
            this.hasBuildingFixes.setValue(this.buildings.value.find(item => item.needToFix.value === true) != null);
        }
    }

    public updateValuesFromClaim(dto: PlayerDto): void {
        // Update from server only if there is a difference.
        if (this.coins.value != dto.coinsBalance) {
            this.coins.setValue(dto.coinsBalance);
        }
        if (this.tokens.value != dto.tokensBalance) {
            this.tokens.setValue(dto.tokensBalance);
        }
        if (this.rolls.value != dto.diceRollsBalance) {
            this.rolls.setValue(dto.diceRollsBalance);
        }
        if (this.level.value != dto.level) {
            this.level.setValue(dto.level);
        }
        if (this.nextLevel.value != dto.nextLevel) {
            this.nextLevel.setValue(dto.nextLevel);
        }
        if (this.upgradePointsBalance.value != dto.upgradePointsBalance) {
            this.upgradePointsBalance.setValue(dto.upgradePointsBalance);
        }
        if (this.requiredPointsBalance.value != dto.requiredPointsBalance) {
            this.requiredPointsBalance.setValue(dto.requiredPointsBalance);
        }
    }
}
